import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'
import { useToast } from '../components/Toast'
import { ROLE_LABELS, canWrite } from '../lib/supabase'
import PostModal from '../components/PostModal'
import Pressroom from '../components/Pressroom'
import ProfileCard from '../components/ProfileCard'
import RankUpBanner from '../components/RankUpBanner'
import { getTierByCi } from '../lib/press'
import { revertPostCI } from '../lib/useColumnInches'

const FORM_URL = 'https://docs.google.com/forms/d/e/1FAIpQLSei7zcQ7kRy7yK-FrBC5EqgaFD1zuYiQJWLsxdwIJFNTnki7w/viewform'
import { REACTIONS, calcResonance } from '../lib/reactions'


// Mini sparkline — shows reaction activity over last 7 days
function Sparkline({ data = [], color = 'var(--accent)', height = 32 }) {
  if (!data.length) return null
  const max = Math.max(...data, 1)
  const w = 80, h = height
  const pts = data.map((v, i) => {
    const x = (i / (data.length - 1)) * w
    const y = h - (v / max) * h * 0.9
    return `${x},${y}`
  }).join(' ')
  return (
    <svg width={w} height={h} style={{ overflow: 'visible' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
      {data.map((v, i) => v > 0 && (
        <circle key={i} cx={(i / (data.length - 1)) * w} cy={h - (v / max) * h * 0.9} r="2" fill={color} />
      ))}
    </svg>
  )
}

export default function DashboardPage() {
  const { user, profile, loading: authLoading } = useAuth()
  const toast = useToast()
  const navigate = useNavigate()
  const [posts, setPosts] = useState([])
  const [reactionData, setReactionData] = useState({}) // postId -> { type: count }
  const [sparklineData, setSparklineData] = useState([]) // last 7 days reaction counts
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('posts') // 'posts' | 'saved' | 'engagement'
  const [savedPosts, setSavedPosts] = useState([])
  const [savedLoading, setSavedLoading] = useState(false)
  const [selectedPostId, setSelectedPostId] = useState(null)
  const [rankUpBanner, setRankUpBanner] = useState(null)
  const [activeTheme, setActiveTheme] = useState('default')
  const [liveCI, setLiveCI] = useState(null) // live column_inches from DB
  const [bio, setBio] = useState('')
  const [bioSaving, setBioSaving] = useState(false)
  const [bioEditing, setBioEditing] = useState(false)

  useEffect(() => {
    if (authLoading) return          // still resolving
    if (!user) { navigate('/auth'); return }
    // auth has settled and user is confirmed — fetch data
    fetchPosts()

    // Realtime CI updates
    const channel = supabase
      .channel('profile-ci-' + user.id)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public', table: 'profiles',
        filter: `id=eq.${user.id}`,
      }, (payload) => {
        if (payload.new?.column_inches !== undefined) setLiveCI(payload.new.column_inches)
      })
      .subscribe()

    return () => supabase.removeChannel(channel)
  }, [authLoading, user?.id])

  useEffect(() => {
    if (profile?.bio !== undefined) setBio(profile.bio || '')
    if (profile?.press_theme) setActiveTheme(profile.press_theme)
  }, [profile])

  async function saveBio() {
    setBioSaving(true)
    const { error } = await supabase.from('profiles').update({ bio }).eq('id', user.id)
    setBioSaving(false)
    if (error) { toast(error.message, 'error'); return }
    setBioEditing(false)
    toast('Bio updated.', 'success')
  }

  async function saveTheme(theme) {
    setActiveTheme(theme)
    await supabase.from('profiles').update({ press_theme: theme }).eq('id', user.id)
  }

  useEffect(() => {
    if (activeTab === 'saved') loadSavedPosts()
    if (activeTab === 'pressroom' && user) {
      supabase.from('profiles').select('column_inches, is_voice, is_founding, press_theme').eq('id', user.id).single()
        .then(({ data }) => {
          if (data) {
            setLiveCI(data.column_inches || 0)
            setActiveTheme(data.press_theme || 'default')
          }
        })
    }
  }, [activeTab, user])

  async function fetchPosts() {
    const { data } = await supabase
      .from('posts')
      .select('*')
      .eq('author_id', user.id)
      .order('created_at', { ascending: false })
    setPosts(data || [])
    // Fetch reactions for all published posts
    const published = (data || []).filter(p => p.status === 'published')
    if (published.length > 0) {
      const ids = published.map(p => p.id)
      const { data: reactions } = await supabase
        .from('reactions')
        .select('post_id, reaction_type')
        .in('post_id', ids)
      const map = {}
      reactions?.forEach(r => {
        if (!map[r.post_id]) map[r.post_id] = {}
        map[r.post_id][r.reaction_type] = (map[r.post_id][r.reaction_type] || 0) + 1
      })
      setReactionData(map)
    }
    // Sparkline: reactions per day for last 7 days across all posts
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString()
    const { data: recentReactions } = await supabase
      .from('reactions')
      .select('created_at')
      .in('post_id', (data || []).filter(p => p.status === 'published').map(p => p.id))
      .gte('created_at', sevenDaysAgo)
    if (recentReactions) {
      const days = Array(7).fill(0)
      recentReactions.forEach(r => {
        const daysAgo = Math.floor((Date.now() - new Date(r.created_at)) / 86400000)
        if (daysAgo < 7) days[6 - daysAgo]++
      })
      setSparklineData(days)
    }
    setLoading(false)
  }

  async function loadSavedPosts() {
    setSavedLoading(true)
    const savedIds = JSON.parse(localStorage.getItem(`margins_saved_${user.id}`) || '[]')
    if (savedIds.length === 0) { setSavedPosts([]); setSavedLoading(false); return }
    const { data } = await supabase
      .from('posts')
      .select('*, profiles!posts_author_id_fkey(id, display_name)')
      .in('id', savedIds)
      .eq('status', 'published')
    setSavedPosts(data || [])
    setSavedLoading(false)
  }

  function removeSaved(id) {
    const key = `margins_saved_${user.id}`
    const list = JSON.parse(localStorage.getItem(key) || '[]')
    localStorage.setItem(key, JSON.stringify(list.filter(i => i !== id)))
    setSavedPosts(prev => prev.filter(p => p.id !== id))
    toast('Removed from reading list.', 'success')
  }

  async function deletePost(id) {
    if (!window.confirm('Delete this post?')) return
    // Optimistic: remove from UI immediately
    const postToDelete = posts.find(p => p.id === id)
    setPosts(prev => prev.filter(p => p.id !== id))
    const { error } = await supabase.from('posts').delete().eq('id', id)
    if (error) {
      // Revert on failure
      setPosts(prev => postToDelete ? [...prev, postToDelete].sort((a,b) => new Date(b.created_at) - new Date(a.created_at)) : prev)
      toast(error.message, 'error')
      return
    }
    if (postToDelete?.author_id && postToDelete.status === 'published') {
      revertPostCI(postToDelete.author_id, postToDelete).catch(console.error)
    }
    toast('Post deleted.', 'success')
  }

  function exportPosts(format) {
    const published = posts.filter(p => p.status === 'published')
    if (published.length === 0) return
    const lines = []
    lines.push(`THE MARGINS — ${profile?.display_name || 'My Writing'}`)
    lines.push(`Exported ${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}`)
    lines.push(`${published.length} piece${published.length !== 1 ? 's' : ''}`)
    lines.push('')
    lines.push('═'.repeat(60))

    published.forEach((p, i) => {
      lines.push('')
      if (format === 'md') lines.push(`# ${p.title}`)
      else lines.push(p.title.toUpperCase())
      lines.push(`${new Date(p.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} · ${p.tag || 'The Feed'} · ~${p.read_time || 1} min read`)
      lines.push('')
      if (p.excerpt) { lines.push(p.excerpt); lines.push('') }
      lines.push('─'.repeat(40))
      lines.push('')
      // Strip HTML tags
      const body = (p.body || '').replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n\n').replace(/<[^>]+>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&nbsp;/g, ' ').trim()
      lines.push(body)
      if (i < published.length - 1) { lines.push(''); lines.push('═'.repeat(60)) }
    })

    const content = lines.join('\n')
    const ext = format === 'md' ? 'md' : 'txt'
    const mime = format === 'md' ? 'text/markdown' : 'text/plain'
    const blob = new Blob([content], { type: mime })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `the-margins-${profile?.display_name?.toLowerCase().replace(/\s+/g, '-') || 'export'}.${ext}`
    a.click()
    URL.revokeObjectURL(url)
    toast('Export downloaded.', 'success')
  }
  const publishedPosts = posts.filter(p => p.status === 'published')
  const totalReactions = publishedPosts.reduce((sum, p) => {
    const r = reactionData[p.id] || {}
    return sum + Object.values(r).reduce((a, b) => a + b, 0)
  }, 0)
  const overallResonance = publishedPosts.length > 0 ? Math.round(
    publishedPosts.reduce((sum, p) => sum + calcResonance(reactionData[p.id] || {}, 0).score, 0) / publishedPosts.length
  ) : 0

  return (
    <div className="dashboard-page">
      {rankUpBanner && (
        <RankUpBanner
          rank={rankUpBanner}
          onDismiss={() => setRankUpBanner(null)}
        />
      )}
      <div className="dashboard-header">
        <div>
          <p style={styles.eyebrow}>My Account</p>
          <h1 style={styles.title}>Dashboard</h1>
        </div>
        {canWrite(profile?.role) && (
          <Link to="/write" className="btn btn-accent">+ New Post</Link>
        )}
      </div>

      <div className="dashboard-body">
        {/* Profile card */}
        <ProfileCard
          displayName={profile?.display_name}
          role={profile?.role}
          bio={bio}
          columnInches={liveCI !== null ? liveCI : (profile?.column_inches || 0)}
          isVoice={profile?.is_voice || false}
          isFounding={profile?.is_founding || false}
          pressTheme={activeTheme}
          size="dashboard"
        >
          <Link to={`/writer/${user?.id}`} className="btn btn-sm btn-ghost">Public Profile</Link>
        </ProfileCard>

        {/* Bio editor */}
        <div style={{ marginBottom: '2rem', padding: '1.25rem 1.5rem', border: '1px solid var(--rule)', background: 'var(--cream)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.6rem' }}>
            <label style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)' }}>
              Bio
            </label>
            {!bioEditing ? (
              <button className="btn btn-sm btn-ghost" onClick={() => setBioEditing(true)} style={{ fontSize: '0.6rem' }}>
                {bio ? 'Edit' : '+ Add Bio'}
              </button>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button className="btn btn-sm btn-ghost" onClick={() => { setBioEditing(false); setBio(profile?.bio || '') }}>Cancel</button>
                <button className="btn btn-sm btn-accent" onClick={saveBio} disabled={bioSaving}>{bioSaving ? 'Saving…' : 'Save'}</button>
              </div>
            )}
          </div>
          {bioEditing ? (
            <textarea
              className="form-textarea"
              value={bio}
              onChange={e => setBio(e.target.value)}
              placeholder="A short bio — who you are, what you think about, what you write. Shown on your public profile."
              style={{ minHeight: 80, fontSize: '0.875rem' }}
              maxLength={400}
            />
          ) : (
            <p style={{ fontSize: '0.875rem', lineHeight: 1.7, color: bio ? 'var(--ink)' : 'var(--muted)', fontStyle: bio ? 'normal' : 'italic' }}>
              {bio || 'No bio yet. Add one to introduce yourself on your profile page.'}
            </p>
          )}
          {bioEditing && (
            <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: 'var(--muted)', marginTop: '0.4rem', textAlign: 'right' }}>
              {bio.length}/400
            </div>
          )}
        </div>

        {!canWrite(profile?.role) && (
          <div className="dashboard-upgrade-box">
            <p style={styles.upgradeText}>start writing for the feed. say something real.</p>
            <Link to="/write" className="btn btn-ink btn-sm">start writing →</Link>
          </div>
        )}

        {/* Tabs */}
        <div style={styles.tabs}>
          {[['posts', 'My Posts'], ['engagement', 'Resonance'], ['saved', 'Reading List'], ['pressroom', 'The Pressroom'], ['export', 'Export']].map(([key, label]) => (
            <button key={key} style={{ ...styles.tab, ...(activeTab === key ? styles.tabActive : {}) }} onClick={() => setActiveTab(key)}>
              {label}
            </button>
          ))}
        </div>

        {/* Posts tab */}
        {activeTab === 'posts' && (
          <div style={styles.postsSection}>
            <div style={styles.featureBanner}>
              <span style={styles.featureBannerIcon}>✎</span>
              <div>
                <div style={styles.featureBannerTitle}>Your pieces</div>
                <div style={styles.featureBannerDesc}>Published pieces can be edited anytime. Drafts are auto-saved every 30 seconds. Use the <strong>✎ Edit</strong> button to update any piece.</div>
              </div>
            </div>
            {loading ? (
              <p style={{ color: 'var(--muted)', fontFamily: "'DM Mono', monospace", fontSize: '0.75rem' }}>Loading...</p>
            ) : posts.length === 0 ? (
              <div className="empty-state"><p>You haven't written anything yet.</p></div>
            ) : (
              <div style={styles.postsList}>
                {posts.map(p => (
                  <div key={p.id} className="dashboard-post-item">
                    <div style={styles.postInfo}>
                      <div style={styles.postTitle}>{p.title}</div>
                      <div style={styles.postMeta}>
                        <span>{'the feed'}</span>
                        <span>·</span>
                        <span>{new Date(p.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                        {p.status === 'published' && (
                          <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>· click Edit to update this piece anytime</span>
                        )}
                        {p.status === 'draft' && (
                          <span style={{ color: 'var(--muted)', fontStyle: 'italic' }}>· click Edit to continue writing</span>
                        )}
                      </div>
                      {/* Rejection note */}
                      {p.status === 'rejected' && p.rejection_note && (
                        <div style={styles.rejectionNote}>
                          <span style={styles.rejectionLabel}>Editor's note:</span> {p.rejection_note}
                        </div>
                      )}
                    </div>
                    <div className="dashboard-post-actions">
                      <span className={`status-badge status-${p.status}`}>{p.status}</span>
                      {p.status === 'published' && (
                        <button className="btn btn-sm btn-ghost" onClick={() => setSelectedPostId(p.id)}>View</button>
                      )}
                      {(p.status === 'published' || p.status === 'draft' || p.status === 'rejected') && (
                        <button
                          className="btn btn-sm btn-accent"
                          onClick={() => navigate(`/write?edit=${p.id}`)}
                          title="Edit this piece — change title, body, tags, or images"
                        >
                          ✎ Edit
                        </button>
                      )}
                      {(p.status === 'draft' || p.status === 'rejected') && (
                        <button className="btn btn-sm btn-danger" onClick={() => deletePost(p.id)}>Delete</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Engagement tab */}
        {activeTab === 'engagement' && (
          <div style={styles.engagementSection}>
            {publishedPosts.length === 0 ? (
              <div className="empty-state"><p>Publish something first to see engagement.</p></div>
            ) : (
              <>
                <div className="dashboard-engagement-summary">
                  <div>
                    <p style={styles.engagementHeading}>Resonance Score</p>
                    <p style={styles.engagementDesc}>
                      Not impressions or views — a weighted measure of whether the writing actually landed. Reactions and comments are combined, with "Brought Me Here" counting most.
                    </p>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'flex-end' }}>
                    <Sparkline data={sparklineData} />
                    <span style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.55rem', color: 'var(--muted)', letterSpacing: '0.06em' }}>last 7 days</span>
                  </div>
                  <div style={styles.engagementStat}>
                    <span style={styles.engagementStatNum}>{overallResonance}</span>
                    <span style={styles.engagementStatLabel}>avg. resonance across {publishedPosts.length} piece{publishedPosts.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                {publishedPosts.map(p => {
                  const r = reactionData[p.id] || {}
                  const total = Object.values(r).reduce((a, b) => a + b, 0)
                  const commentCount = 0 // could fetch separately
                  return (
                    <div key={p.id} style={styles.engagementCard}>
                      <div style={styles.engagementCardHeader}>
                        <div style={styles.engagementTitle}>{p.title}</div>
                        <button style={styles.viewBtn} className="btn btn-sm btn-ghost" onClick={() => setSelectedPostId(p.id)}>Read</button>
                      </div>
                      {(() => {
                        const resonance = calcResonance(r, 0)
                        return (
                          <>
                            {resonance.score > 0 && (
                              <div style={styles.resonanceRow}>
                                <div style={styles.resonanceBarWrap}>
                                  <div style={{ ...styles.resonanceBar, width: resonance.score + '%' }} />
                                </div>
                                <span style={styles.resonanceScore}>{resonance.score}</span>
                                <span style={styles.resonanceLabel}>{resonance.label}</span>
                              </div>
                            )}
                            {total === 0 ? (
                              <p style={styles.noReactions}>No reactions yet.</p>
                            ) : (
                              <div style={styles.reactionBreakdown}>
                                {REACTIONS.map(rx => r[rx.key] > 0 ? (
                                  <div key={rx.key} style={styles.reactionItem}>
                                    <span style={styles.reactionSymbol}>{rx.symbol}</span>
                                    <span style={styles.reactionItemLabel}>{rx.label}</span>
                                    <span style={styles.reactionItemCount}>{r[rx.key]}</span>
                                  </div>
                                ) : null)}
                              </div>
                            )}
                          </>
                        )
                      })()}
                    </div>
                  )
                })}
              </>
            )}
          </div>
        )}

        {/* Saved / Reading list tab */}
        {activeTab === 'saved' && (
          <div style={styles.postsSection}>
            {savedLoading ? (
              <p style={{ color: 'var(--muted)', fontFamily: "'DM Mono', monospace", fontSize: '0.75rem' }}>Loading...</p>
            ) : savedPosts.length === 0 ? (
              <div className="empty-state">
                <p>Your reading list is empty.</p>
                <p style={{ fontSize: '0.8rem', color: 'var(--muted)', marginTop: '0.5rem' }}>Save pieces by clicking ◇ inside any post.</p>
              </div>
            ) : (
              <div style={styles.postsList}>
                {savedPosts.map(p => (
                  <div key={p.id} className="dashboard-post-item">
                    <div style={styles.postInfo}>
                      <div style={styles.postTitle}>{p.title}</div>
                      <div style={styles.postMeta}>
                        <span>{p.profiles?.display_name}</span>
                        <span>·</span>
                        <span>{'the feed'}</span>
                      </div>
                    </div>
                    <div className="dashboard-post-actions">
                      <button className="btn btn-sm btn-ghost" onClick={() => setSelectedPostId(p.id)}>Read</button>
                      <button className="btn btn-sm btn-danger" onClick={() => removeSaved(p.id)}>Remove</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {/* Pressroom tab */}
        {activeTab === 'pressroom' && (
          <Pressroom
            columnInches={liveCI !== null ? liveCI : (profile?.column_inches || 0)}
            isVoice={profile?.is_voice || false}
            activeTheme={activeTheme}
            onThemeChange={saveTheme}
          />
        )}

        {/* Export tab */}
        {activeTab === 'export' && (
          <div style={{ padding: '0.5rem 0' }}>
            <div style={styles.featureBanner}>
              <span style={styles.featureBannerIcon}>↓</span>
              <div>
                <div style={styles.featureBannerTitle}>Export Your Work</div>
                <div style={styles.featureBannerDesc}>
                  Your writing is yours. Download all your published pieces as a plain text file you can keep forever, import elsewhere, or share.
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', marginTop: '1rem' }}>
              <button
                className="btn btn-ink"
                onClick={() => exportPosts('txt')}
                disabled={posts.filter(p => p.status === 'published').length === 0}
              >
                ↓ Download as Plain Text
              </button>
              <button
                className="btn btn-ghost"
                onClick={() => exportPosts('md')}
                disabled={posts.filter(p => p.status === 'published').length === 0}
              >
                ↓ Download as Markdown
              </button>
            </div>
            {posts.filter(p => p.status === 'published').length === 0 && (
              <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'var(--muted)', marginTop: '1rem' }}>
                Publish something first to export.
              </p>
            )}
            <p style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', color: 'var(--muted)', marginTop: '1.25rem', letterSpacing: '0.04em', lineHeight: 1.7 }}>
              {posts.filter(p => p.status === 'published').length} published piece{posts.filter(p => p.status === 'published').length !== 1 ? 's' : ''} will be included.
              Drafts and rejected pieces are not exported.
            </p>
          </div>
        )}
      </div>

      {selectedPostId && (
        <PostModal
          postId={selectedPostId}
          onClose={() => setSelectedPostId(null)}
          onNavigateWriter={id => { setSelectedPostId(null); navigate('/writer/' + id) }}
        />
      )}
    </div>
  )
}

function getRoleColor(role) {
  const colors = { viewer: 'var(--muted)', writer_blog: '#4a90d9', writer_all: '#7b5ea7', mod: 'var(--warning)', admin: 'var(--accent)' }
  return colors[role] || 'var(--muted)'
}

const styles = {
  page: { paddingTop: '4.5rem', minHeight: '100vh' },
  header: { padding: '2.5rem 3rem', borderBottom: '1px solid var(--rule)', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  eyebrow: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.5rem' },
  title: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.8rem', fontWeight: 700 },
  body: { padding: '3rem', maxWidth: 860, margin: '0 auto' },
  profileCard: { display: 'flex', alignItems: 'center', gap: '1.5rem', padding: '1.75rem 2rem', background: 'var(--cream)', border: '1px solid var(--rule)', marginBottom: '2rem' },
  avatar: { width: 52, height: 52, background: 'var(--ink)', color: 'var(--paper)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 700, borderRadius: '50%', flexShrink: 0 },
  profileName: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1.3rem', fontWeight: 700, marginBottom: '0.2rem' },
  profileEmail: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'var(--muted)' },
  roleBadge: { textAlign: 'right', display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  upgradeBox: { padding: '1.5rem 2rem', border: '1px dashed var(--rule)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '2rem', marginBottom: '2rem' },
  upgradeText: { fontSize: '0.9rem', color: 'var(--muted)', lineHeight: 1.6 },
  tabs: { display: 'flex', gap: 0, borderBottom: '1px solid var(--rule)', marginBottom: '2rem' },
  tab: { background: 'none', border: 'none', borderBottom: '2px solid transparent', padding: '0.75rem 1.25rem', fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.1em', textTransform: 'uppercase', cursor: 'pointer', color: 'var(--muted)', transition: 'all 0.15s', marginBottom: -1 },
  tabActive: { borderBottomColor: 'var(--ink)', color: 'var(--ink)' },
  postsSection: {},
  postsList: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
  postItem: { background: 'var(--paper)', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', border: '1px solid var(--rule)', borderRadius: 8 },
  postInfo: { flex: 1 },
  postTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700, marginBottom: '0.3rem' },
  postMeta: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', color: 'var(--muted)', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' },
  featureBanner: {
    display: 'flex', gap: '1rem', alignItems: 'flex-start',
    padding: '1rem 1.25rem', marginBottom: '1rem',
    background: 'rgba(200,64,26,0.06)', border: '1px solid rgba(200,64,26,0.2)',
    borderRadius: 8,
  },
  featureBannerIcon: { fontSize: '1.2rem', color: 'var(--accent)', flexShrink: 0, marginTop: 2 },
  featureBannerTitle: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', fontWeight: 600, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.25rem' },
  featureBannerDesc: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.8rem', color: 'var(--muted)', lineHeight: 1.6 },
  postActions: { display: 'flex', gap: '0.5rem', alignItems: 'center', flexShrink: 0 },
  rejectionNote: { marginTop: '0.5rem', padding: '0.6rem 0.9rem', background: 'rgba(232,101,58,0.08)', border: '1px solid rgba(232,101,58,0.2)', fontSize: '0.8rem', color: 'var(--muted)', lineHeight: 1.5 },
  rejectionLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--accent)', marginRight: '0.4rem' },
  // Engagement
  engagementSection: {},
  engagementSummary: { padding: '1.5rem 2rem', background: 'var(--cream)', border: '1px solid var(--rule)', marginBottom: '1.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  engagementDesc: { fontSize: '0.875rem', color: 'var(--muted)', lineHeight: 1.65, maxWidth: '55ch' },
  engagementStat: { textAlign: 'right', flexShrink: 0 },
  engagementStatNum: { display: 'block', fontFamily: "'Space Grotesk', sans-serif", fontSize: '2.5rem', fontWeight: 700, color: 'var(--accent)', lineHeight: 1 },
  engagementStatLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', color: 'var(--muted)', letterSpacing: '0.08em' },
  engagementHeading: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '0.5rem' },
  resonanceRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' },
  resonanceBarWrap: { flex: 1, height: 3, background: 'var(--rule)', overflow: 'hidden' },
  resonanceBar: { height: '100%', background: 'var(--accent)', transition: 'width 0.6s ease' },
  resonanceScore: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', fontWeight: 700, color: 'var(--ink)', minWidth: 24, textAlign: 'right' },
  resonanceLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.58rem', color: 'var(--muted)', letterSpacing: '0.06em', minWidth: 130 },
  engagementCard: { padding: '1.5rem 2rem', background: 'var(--paper)', border: '1px solid var(--rule)', borderTop: 'none', marginTop: 0 },
  engagementCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' },
  engagementTitle: { fontFamily: "'Space Grotesk', sans-serif", fontSize: '1rem', fontWeight: 700, flex: 1, marginRight: '1rem' },
  viewBtn: {},
  noReactions: { fontFamily: "'DM Mono', monospace", fontSize: '0.65rem', color: 'var(--muted)', letterSpacing: '0.06em' },
  reactionBreakdown: { display: 'flex', gap: '1rem', flexWrap: 'wrap' },
  reactionItem: { display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.35rem 0.75rem', border: '1px solid var(--rule)', background: 'var(--cream)' },
  reactionSymbol: { fontSize: '0.85rem' },
  reactionItemLabel: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', letterSpacing: '0.06em', color: 'var(--muted)' },
  reactionItemCount: { fontFamily: "'DM Mono', monospace", fontSize: '0.62rem', fontWeight: 700, color: 'var(--ink)', marginLeft: '0.2rem' },
}
